import { motion } from "framer-motion";
import { ArrowRight, Clock, Network } from "lucide-react";
import { Link } from "react-router-dom";
import {
  FrequencyDisplay,
  StatusBadge,
  TechButton,
  TechCard,
} from "../components/tech";
import { REPEATER_INFO, TECHNICAL_PARAMS, TIMESLOTS } from "../data/repeater";

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export default function HomePage() {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-12 flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-accent-primary/10 via-transparent to-transparent" />

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Callsign */}
            <motion.h1
              className="text-6xl md:text-8xl font-display font-bold mb-4"
              style={{
                background:
                  "linear-gradient(135deg, #58A6FF 0%, #79C0FF 50%, #F78166 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 0 60px rgba(88, 166, 255, 0.5)",
              }}
            >
              {REPEATER_INFO.callsign}
            </motion.h1>

            {/* Location */}
            <motion.p
              className="text-2xl md:text-3xl font-display text-text-primary mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {REPEATER_INFO.location}
            </motion.p>

            {/* Description */}
            <motion.p
              className="text-lg text-text-secondary mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {REPEATER_INFO.description}
            </motion.p>

            {/* Feature line */}
            <motion.div
              className="glass-morphism inline-block px-6 py-3 rounded-full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-sm text-text-secondary">
                {REPEATER_INFO.featureDescription}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Repeater Information Section */}
      <section className="py-12 px-4 bg-bg-elevated/30">
        <div className="max-w-xl mx-auto">
          {/* Title */}
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl font-display font-bold text-text-primary mb-6 text-center"
          >
            Repeater Information
          </motion.h2>

          {/* Combined Frequency Display + Timeslots Card */}
          <TechCard>
            {/* Frequency Display */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <FrequencyDisplay
                rx={`${TECHNICAL_PARAMS.rxFrequency} MHz`}
                tx={`${TECHNICAL_PARAMS.txFrequency} MHz`}
                tone="N/A (DMR)"
                offset="-5.0 MHz"
                visualizer={true}
              />
            </motion.div>

            {/* Divider */}
            <div className="border-t border-border-primary my-6" />

            {/* Timeslot Assignments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-4">
                <div className="bg-accent-primary/20 p-2 rounded-lg mr-3">
                  <Clock className="w-5 h-5 text-accent-primary" />
                </div>
                <h3 className="text-lg font-display font-semibold text-text-primary">
                  Timeslot Assignments
                </h3>
              </div>
              <div className="space-y-3">
                {Object.entries(TIMESLOTS).map(([key, slot]) => (
                  <div key={key} className="p-3 bg-bg-elevated/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-accent-primary">
                        {slot.name}
                      </span>
                      <StatusBadge
                        status={
                          slot.type === "Dynamic" ? "online" : "maintenance"
                        }
                        label={slot.type}
                        size="sm"
                      />
                    </div>
                    <p className="text-text-primary text-sm">
                      {slot.description}
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      {slot.details}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </TechCard>
        </div>
      </section>

      {/* WIRES-X Node Announcement */}
      <section className="py-8 px-4">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="glass-morphism p-6 rounded-lg border border-accent-primary/20">
              <div className="flex items-start gap-4">
                <div className="bg-accent-primary/20 p-3 rounded-lg flex-shrink-0">
                  <Network className="w-6 h-6 text-accent-primary" />
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-display font-semibold text-text-primary mb-2">
                    New: WIRES-X Simplex Node
                  </h3>
                  <p className="text-sm text-text-secondary mb-4">
                    WIRES-X simplex node now online on{" "}
                    <span className="font-mono font-semibold text-accent-primary">
                      145.785 MHz
                    </span>{" "}
                    (C4FM DN). Access internet-linked WIRES-X rooms via simplex.
                    Click for details and FT-70D connection steps.
                  </p>
                  <Link to="/wiresx">
                    <TechButton variant="secondary">
                      WIRES-X Node Details{" "}
                      <ArrowRight className="w-4 h-4 ml-2 inline" />
                    </TechButton>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
